import React from "react";
import { AlertTriangle, Bell, BellOff, X } from "lucide-react";
import { useTranslation } from "react-i18next";

/**
 * Modal de confirmation pour la suppression / annulation d'un match.
 *
 * Props :
 *  - isDarkMode : boolean
 *  - matchLabel : string (ex: "vs PSG")
 *  - matchDate  : string (ex: "2026-02-20")
 *  - onConfirmWithNotify : () => void   → supprime + notifie les joueurs
 *  - onConfirmWithoutNotify : () => void → supprime sans notifier
 *  - onCancel : () => void
 *  - loading : boolean
 */
const MatchCancelConfirmModal = ({
  isDarkMode,
  matchLabel = "",
  matchDate = "",
  onConfirmWithNotify,
  onConfirmWithoutNotify,
  onCancel,
  loading = false,
}) => {
  const { t } = useTranslation();
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60]">
      <div
        className={`relative p-6 rounded-xl shadow-2xl w-[440px] max-w-[95vw] ${
          isDarkMode
            ? "bg-gray-800 text-white border border-gray-700"
            : "bg-white text-gray-900 border border-gray-200"
        }`}
      >
        {/* Close */}
        <button
          onClick={onCancel}
          className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-gray-600/30 transition"
        >
          <X size={18} />
        </button>

        {/* Icon + Title */}
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2.5 rounded-full bg-red-500/15">
            <AlertTriangle size={24} className="text-red-500" />
          </div>
          <div>
            <h3 className="text-lg font-bold">{t("calendar_ui.delete_match_title")}</h3>
            {matchLabel && (
              <p className="text-sm opacity-70">{matchLabel}</p>
            )}
            {matchDate && (
              <p className="text-xs opacity-50">{matchDate}</p>
            )}
          </div>
        </div>

        {/* Description */}
        <p className={`text-sm mb-5 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>
          {t("calendar_ui.delete_match_body")}
        </p>

        {/* Buttons */}
        <div className="flex flex-col gap-2">
          <button
            onClick={onConfirmWithNotify}
            disabled={loading}
            className={`flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-lg font-medium text-white transition ${
              loading
                ? "bg-red-400 cursor-not-allowed"
                : "bg-red-600 hover:bg-red-700"
            }`}
          >
            <Bell size={16} />
            {loading ? t("calendar_ui.deleting") : t("calendar_ui.delete_and_notify")}
          </button>

          <button
            onClick={onConfirmWithoutNotify}
            disabled={loading}
            className={`flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-lg font-medium transition ${
              loading
                ? "bg-gray-400 cursor-not-allowed text-white"
                : isDarkMode
                  ? "bg-gray-700 text-white hover:bg-gray-600"
                  : "bg-gray-200 text-gray-800 hover:bg-gray-300"
            }`}
          >
            <BellOff size={16} />
            {loading ? t("calendar_ui.deleting") : t("calendar_ui.delete_without_notify")}
          </button>

          <button
            onClick={onCancel}
            disabled={loading}
            className={`w-full px-4 py-2 rounded-lg text-sm transition ${
              isDarkMode
                ? "text-gray-400 hover:text-white"
                : "text-gray-500 hover:text-gray-800"
            }`}
          >
            {t("common.cancel")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MatchCancelConfirmModal;
